import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTE_NAMES } from '../../routes/routeConstants';
import { useAuth } from '../../provider/AuthProvider';
import { useUser } from '../../provider/UserProvider';
import { useUserStore } from '../../store/useUserStore';
import { getSavedJobs } from '../../services/savedJobsService';
import { fetchJobById } from '../../services/jobService';
import { Job } from '../../models/Job';
import { ConfirmationDialog, SubjectMultiSelect, StudyLevelMultiSelect } from '../../components/shared/ui';
import { ACADEMIC_ROLES } from '../../data/academicTaxonomy';
import { fetchRecommendations, MatchedJob, ProfileCompleteness } from '../../services/matchingService';
import { getUserApplications, Application } from '../../services/applicationService';
import { VELLUM as T, SANS, MONO, display, mono, btn } from '../../components/shared/folio/kit';
import { FolioSelect } from '../../components/shared/folio/Select';

const HE_RECOGNITION_OPTIONS = ['None', 'Associate Fellow', 'Fellow', 'Senior Fellow'];
const HEARD_ABOUT_OPTIONS = ['LinkedIn', 'Facebook', 'Referral', 'Instagram', 'Other'];
import SheetGround from '../../components/shared/ui/SheetGround';

/* The folio vocabulary mapped onto the token names this page already used.
   A dashboard is an Operate surface: the sheet supplies the material, but
   scanning and density win over expression, so the change here is palette,
   type and rule weight - not a re-plan of the page. Nothing about how it
   loads, filters, saves or uploads is touched. */
const pal = {
  paper: T.ground,
  card: T.ground,
  ink: T.ink,
  inkSoft: T.mute,
  inkMute: T.mute,
  rule: T.rule,
  ruleSoft: T.rule,
  accent: T.accent,
};

const fam = { sans: SANS, mono: MONO, serif: SANS };

const acaLabel: React.CSSProperties = {
  fontFamily: fam.mono,
  fontSize: 10.5,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: pal.inkSoft,
};
/* A ruled cell, not a card: the sheet has one ground and no elevation, so
   grouping is done with a hairline. */
const acaCard: React.CSSProperties = {
  background: pal.card,
  border: `1px solid ${pal.rule}`,
  padding: 24,
};

const JobSeekerDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { getProfile, updateProfile, loading: profileLoading } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read initial tab from URL query parameter
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [profileData, setProfileData] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [savedJobs, setSavedJobs] = useState<Job[]>([]);
  const [savedJobsLoading, setSavedJobsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const { toggleSavedJob } = useUserStore();
  const [recommendedJobs, setRecommendedJobs] = useState<MatchedJob[]>([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [profileCompleteness, setProfileCompleteness] = useState<ProfileCompleteness | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationFilter, setApplicationFilter] = useState<string | null>(null);

  // Update active tab when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['overview', 'applications', 'saved', 'recommendations', 'profile'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Auto-load profile data when profile tab is selected
  useEffect(() => {
    if (activeTab === 'profile' && !profileData) {
      loadProfileData();
    }
  }, [activeTab]);

  // Auto-load saved jobs when saved tab is selected
  useEffect(() => {
    if (activeTab === 'saved' && savedJobs.length === 0) {
      loadSavedJobs();
    }
  }, [activeTab]);

  // Load saved jobs and applications on component mount for overview stats
  useEffect(() => {
    loadSavedJobs();
    loadApplications();
  }, []);

  // Load recommendations when tab is active
  useEffect(() => {
    if ((activeTab === 'recommendations' || activeTab === 'overview') && recommendedJobs.length === 0) {
      loadRecommendations();
    }
  }, [activeTab]);

  const loadRecommendations = async () => {
    try {
      setRecommendationsLoading(true);
      const data = await fetchRecommendations(1, 10);
      setRecommendedJobs(data.recommendations);
      setProfileCompleteness(data.profileCompleteness);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const loadApplications = async () => {
    try {
      setApplicationsLoading(true);
      const data = await getUserApplications();
      setApplications(data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const loadSavedJobs = async () => {
    try {
      setSavedJobsLoading(true);
      const savedJobsData = await getSavedJobs();
      
      // Fetch full job details for each saved job
      const jobsWithDetails = await Promise.all(
        savedJobsData.map(async (savedJob) => {
          try {
            const jobDetails = await fetchJobById(savedJob.jobId);
            return jobDetails;
          } catch (error) {
            console.error(`Failed to fetch job details for ${savedJob.jobId}:`, error);
            return null;
          }
        })
      );
      
      // Filter out null results and set the jobs
      const validJobs = jobsWithDetails.filter((job): job is Job => job !== null);
      setSavedJobs(validJobs);
    } catch (error) {
      console.error('Failed to load saved jobs:', error);
    } finally {
      setSavedJobsLoading(false);
    }
  };

  const loadProfileData = async () => {
    try {
      const profile = await getProfile();
      setProfileData(profile);
      setEditFormData(profile || {});
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditFormData(profileData || {});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditFormData(profileData || {});
  };

  const handleSave = async () => {
    try {
      await updateProfile(editFormData);
      setProfileData(editFormData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setEditFormData((prev: any) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRemoveSavedJob = (job: Job) => {
    setJobToDelete(job);
    setShowDeleteDialog(true);
  };

  const confirmRemoveSavedJob = async () => {
    if (!jobToDelete) return;
    
    try {
      // Remove from backend
      const { unsaveJob } = await import('../../services/savedJobsService');
      await unsaveJob(jobToDelete.id);
      
      // Remove from local state
      setSavedJobs(prev => prev.filter(job => job.id !== jobToDelete.id));
      
      // Update user store
      toggleSavedJob(jobToDelete.id);
      
    } catch (error) {
      console.error('Failed to remove saved job:', error);
    } finally {
      setJobToDelete(null);
    }
  };

  const stats = {
    applicationsSubmitted: applications.length,
    jobsSaved: savedJobs.length,
    profileViews: 0
  };

  // Helper to extract job details from populated application
  const getJobInfo = (app: Application) => {
    const job = typeof app.jobId === 'string' ? null : app.jobId;
    return {
      title: job?.title || 'Unknown Job',
      company: job?.company || '',
      location: job?.location || '',
    };
  };

  const getDaysAgo = (dateStr: string) => {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', code: '01' },
    { id: 'applications', label: 'Applications', code: '02' },
    { id: 'saved', label: 'Saved Jobs', code: '03' },
    { id: 'recommendations', label: 'Recommendations', code: '04' },
    { id: 'profile', label: 'Profile', code: '05' }
  ];

  return (
    <SheetGround className="jd-root" style={{ color: pal.ink, fontFamily: fam.sans, minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ borderBottom: `1px solid ${pal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 20px 36px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: 16,
              borderBottom: `1px solid ${pal.rule}`,
              paddingBottom: 14,
              ...acaLabel,
              marginBottom: 28,
            }}
          >
            <div style={{ gridColumn: 'span 4' }}>§ Dashboard · Scholar</div>
            <div style={{ gridColumn: 'span 4', textAlign: 'center' }}>{user?.firstName || 'You'} · {user?.email || ''}</div>
            <div style={{ gridColumn: 'span 4', textAlign: 'right' }}>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>
          {/* The code sits on the heading's baseline rather than above it:
              a label stacked over a heading is a kicker. */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <h1 style={{ ...display(T, 'clamp(38px, 5.8vw, 62px)'), lineHeight: 0.96, letterSpacing: '-0.04em' }}>
              Track your{' '}
              <span style={{ color: pal.accent, borderBottom: `3px solid ${pal.accent}` }}>applications</span>.
            </h1>
            <span style={mono(T, T.accent)}>§ Your register</span>
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTE_NAMES.PAGES.JOBS)}
            className="jd-accent"
            style={{ ...btn(T, 'accent'), marginTop: 26 }}
          >
            Browse jobs
          </button>
        </div>
      </section>

      {/* Body */}
      <section>
        <div
          className="jd-grid"
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: '40px 20px 80px',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 1px 296px',
            columnGap: 40,
            alignItems: 'start',
          }}
        >
          <div
            className="jd-spine"
            aria-hidden
            style={{ gridColumn: 2, gridRow: '1 / -1', alignSelf: 'stretch', width: 1, background: pal.rule }}
          />

          {/* Navigate - the margin apparatus, drawn and pinned as it is on
              every other sheet. It stays first in the DOM so the keyboard
              reaches it before the panel it controls, and so it leads on a
              phone where the tracks stack. */}
          {/* Both tracks are pinned to row 1 explicitly. The margin comes
              first in the DOM, so without a row the measure would be auto-
              placed into row 2 and start below it. */}
          <aside className="jd-margin" style={{ gridColumn: 3, gridRow: 1, alignSelf: 'stretch' }}>
            <div className="jd-sticky">
              <div style={{ ...mono(T, T.accent), paddingBottom: 10, borderBottom: `1.5px solid ${pal.ink}` }}>
                § Navigate
              </div>
              <nav>
                {tabs.map(tab => {
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      aria-current={active ? 'true' : undefined}
                      className="jd-nav"
                      style={{
                        ...mono(T, active ? T.ink : T.mute),
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: '14px minmax(0,1fr) auto',
                        gap: 10,
                        alignItems: 'center',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: `1px solid ${pal.rule}`,
                        borderRadius: 0,
                        padding: '12px 0',
                        cursor: 'pointer',
                        fontWeight: active ? 600 : 500,
                      }}
                    >
                      {/* A registration mark on the panel in hand. */}
                      <span
                        aria-hidden
                        style={{
                          width: active ? 7 : 9,
                          height: active ? 7 : 1,
                          background: active ? T.accent : T.rule,
                          justifySelf: 'start',
                        }}
                      />
                      <span>{tab.label}</span>
                      <span style={{ color: active ? T.accent : T.mute }}>{tab.code}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="jd-measure" style={{ gridColumn: 1, gridRow: 1 }}>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid md:grid-cols-3 gap-4">
                  <button onClick={() => { setApplicationFilter(null); setActiveTab('applications'); }} style={{ ...acaCard, textAlign: 'left', cursor: 'pointer' }}>
                    <div className="flex items-center gap-3">
                      
                      <div>
                        <p className="jd-figure" style={{ color: pal.ink }}>
                          {stats.applicationsSubmitted}
                        </p>
                        <p className="text-sm jd-mute">Applications</p>
                      </div>
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('saved')} style={{ ...acaCard, textAlign: 'left', cursor: 'pointer' }}>
                    <div className="flex items-center gap-3">
                      
                      <div>
                        <p className="jd-figure" style={{ color: pal.ink }}>
                          {stats.jobsSaved}
                        </p>
                        <p className="text-sm jd-mute">Saved Jobs</p>
                      </div>
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('profile')} style={{ ...acaCard, textAlign: 'left', cursor: 'pointer' }}>
                    <div className="flex items-center gap-3">
                      
                      <div>
                        <p className="jd-figure" style={{ color: pal.ink }}>
                          {stats.profileViews}
                        </p>
                        <p className="text-sm jd-mute">Profile Views</p>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Recent Applications */}
                <div style={acaCard}>
                  <h2 className="jd-h3 mb-4" style={{ color: pal.ink }}>
                    Recent Applications
                  </h2>
                  {applicationsLoading ? (
                    <div className="animate-pulse space-y-3">
                      {[1, 2].map((i) => (
                        <div key={i} className="h-16 jd-panel" />
                      ))}
                    </div>
                  ) : applications.length === 0 ? (
                    <p className="jd-mute text-center py-6">No applications yet. Browse jobs and apply!</p>
                  ) : (
                    <div className="space-y-4">
                      {applications.slice(0, 3).map(app => {
                        const job = getJobInfo(app);
                        return (
                          <div key={app._id} className="flex items-center justify-between p-4 border">
                            <div>
                              <h3 className="font-semibold">{job.title}</h3>
                              <p className="text-sm jd-mute">{job.company}</p>
                              <p className="text-sm jd-mute">{getDaysAgo(app.appliedAt)}</p>
                            </div>
                            <span className={`${
                              app.status === 'rejected'
                                ? 'jd-chip jd-chip-no'
                                : app.status === 'accepted'
                                ? 'jd-chip jd-chip-yes'
                                : 'jd-chip jd-chip-wait'
                            }`}>
                              {app.status === 'accepted' || app.status === 'rejected' ? app.status : 'pending'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Recommended Jobs */}
                <div style={acaCard}>
                  <h2 className="jd-h3 mb-4" style={{ color: pal.ink }}>
                    Recommended for You
                  </h2>
                  {recommendationsLoading ? (
                    <div className="text-center py-6">
                      <div className="animate-pulse space-y-3">
                        {[1, 2].map((i) => (
                          <div key={i} className="h-16 jd-panel" />
                        ))}
                      </div>
                    </div>
                  ) : recommendedJobs.length === 0 ? (
                    <p className="jd-mute text-center py-6">Complete your profile to get personalized recommendations.</p>
                  ) : (
                    <div className="space-y-4">
                      {recommendedJobs.slice(0, 3).map(({ job, matchScore, matchReasons }) => (
                        <div key={job.id} className="flex items-center justify-between p-4 border">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{job.title}</h3>
                              {job.hot && <span className="jd-hot">Hot</span>}
                            </div>
                            <p className="text-sm jd-mute">{job.company} • {job.location}</p>
                            <p className="text-sm jd-mute">{job.salary}</p>
                            {matchReasons.length > 0 && (
                              <p className="text-xs jd-mute mt-1">{matchReasons[0]}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm font-medium" style={{ color: pal.accent }}>
                                {matchScore}% match
                              </p>
                            </div>
                            <button
                              onClick={() => navigate(`/jobs/${job.id}`)}
                              className="px-4 py-2 jd-on font-medium transition-all hover:opacity-90"
                              style={{ backgroundColor: pal.accent }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'applications' && (() => {
              // Coalesce legacy statuses (under_review, interview_scheduled) into 'pending' for filtering.
              const displayStatus = (s: string) =>
                s === 'accepted' || s === 'rejected' ? s : 'pending';
              const filteredApplications = applicationFilter
                ? applications.filter(a => displayStatus(a.status) === applicationFilter)
                : applications;
              const filterLabel = applicationFilter
                ? applicationFilter.charAt(0).toUpperCase() + applicationFilter.slice(1)
                : null;

              return (
              <div style={{ ...acaCard, padding: 32 }}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="jd-h2" style={{ color: pal.ink }}>
                    {filterLabel ? `${filterLabel} (${filteredApplications.length})` : `My Applications (${applications.length})`}
                  </h2>
                  {applicationFilter && (
                    <button
                      onClick={() => setApplicationFilter(null)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all hover:opacity-80"
                      style={{ backgroundColor: pal.card, color: pal.inkSoft }}
                    >
                      Show All Applications
                      <span className="jd-mute">×</span>
                    </button>
                  )}
                </div>
                {/* Status filter tabs */}
                <div className="flex gap-2 mb-6 flex-wrap">
                  {[
                    { value: null, label: 'All' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'accepted', label: 'Accepted' },
                    { value: 'rejected', label: 'Rejected' },
                  ].map(filter => {
                    const count = filter.value
                      ? applications.filter(a => displayStatus(a.status) === filter.value).length
                      : applications.length;
                    return (
                      <button
                        key={filter.label}
                        onClick={() => setApplicationFilter(filter.value)}
                        className={`px-3 py-1.5 text-sm font-medium transition-all ${
                          applicationFilter === filter.value
                            ? 'jd-on'
                            : 'hover:opacity-80'
                        }`}
                        style={
                          applicationFilter === filter.value
                            ? { backgroundColor: pal.accent }
                            : { backgroundColor: pal.card, color: pal.inkSoft }
                        }
                      >
                        {filter.label} ({count})
                      </button>
                    );
                  })}
                </div>
                {applicationsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: pal.accent }}></div>
                      <p className="jd-mute">Loading applications...</p>
                    </div>
                  </div>
                ) : filteredApplications.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="jd-empty-mark" aria-hidden />
                    <h3 className="jd-h3 mb-2" style={{ color: pal.ink }}>
                      {applicationFilter ? `No ${filterLabel} applications` : 'No Applications Yet'}
                    </h3>
                    <p className="jd-mute mb-6">
                      {applicationFilter
                        ? 'No applications match this filter.'
                        : 'Start applying for jobs to track your applications here.'}
                    </p>
                    {!applicationFilter && (
                      <a
                        href="/jobs"
                        className="inline-flex items-center px-6 py-3 jd-on font-medium transition-all hover:opacity-90"
                        style={{ backgroundColor: pal.accent }}
                      >
                        Browse Jobs
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredApplications.map(app => {
                      const job = getJobInfo(app);
                      const jobId = typeof app.jobId === 'string' ? app.jobId : app.jobId?._id;
                      return (
                        <div key={app._id} className="p-6 border">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="jd-h4 mb-2">{job.title}</h3>
                              <p className="jd-mute mb-2">{job.company}{job.location ? ` • ${job.location}` : ''}</p>
                              <p className="text-sm jd-mute">Applied {getDaysAgo(app.appliedAt)}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`${
                                app.status === 'rejected'
                                  ? 'jd-chip jd-chip-no'
                                  : app.status === 'accepted'
                                  ? 'jd-chip jd-chip-yes'
                                  : 'jd-chip jd-chip-wait'
                              }`}>
                                {displayStatus(app.status)}
                              </span>
                              {jobId && (
                                <button
                                  onClick={() => navigate(`/jobs/${jobId}`)}
                                  className="px-4 py-2 border font-medium transition-all hover:opacity-80"
                                  style={{ borderColor: pal.rule, color: pal.inkSoft }}
                                >
                                  View Job
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })()}

            {activeTab === 'saved' && (
              <div style={{ ...acaCard, padding: 32 }}>
                <h2 className="jd-h2 mb-6" style={{ color: pal.ink }}>
                  Saved Jobs ({savedJobs.length})
                </h2>
                
                {savedJobsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: pal.accent }}></div>
                      <p className="jd-mute">Loading saved jobs...</p>
                    </div>
                  </div>
                ) : savedJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="jd-empty-mark" aria-hidden />
                    <h3 className="jd-h3 mb-2" style={{ color: pal.ink }}>
                      No Saved Jobs
                    </h3>
                    <p className="jd-mute mb-6">
                      Start saving jobs you're interested in by clicking the save button on job listings.
                    </p>
                    <a 
                      href="/jobs" 
                      className="inline-flex items-center px-6 py-3 jd-on font-medium transition-all hover:opacity-90"
                      style={{ backgroundColor: pal.accent }}
                    >
                      Browse Jobs
                    </a>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedJobs.map(job => (
                      <div key={job.id} className="p-6 border hover: transition-shadow" style={{ borderColor: pal.rule }}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="jd-h4" style={{ color: pal.ink }}>
                                {job.title}
                              </h3>
                              {job.hot && (
                                <span className="jd-hot">
                                  Hot
                                </span>
                              )}
                            </div>
                            <p className="jd-mute mb-2">{job.company} • {job.location}</p>
                            <p className="text-sm jd-mute">{job.salary} • {job.employmentType} • {job.experience}</p>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleRemoveSavedJob(job)}
                              className="px-4 py-2 border font-medium transition-all hover:opacity-80" 
                              style={{ borderColor: pal.rule, color: pal.inkSoft }}
                            >
                              Remove
                            </button>
                            <a 
                              href={`/jobs/${job.id}`}
                              className="px-4 py-2 jd-on font-medium transition-all hover:opacity-90"
                              style={{ backgroundColor: pal.accent }}
                            >
                              View Details
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'recommendations' && (
              <div style={{ ...acaCard, padding: 32 }}>
                <h2 className="jd-h2 mb-6" style={{ color: pal.ink }}>
                  Job Recommendations
                </h2>

                {/* Profile completeness suggestions */}
                {profileCompleteness && profileCompleteness.suggestions.length > 0 && (
                  <div className="mb-6 p-4" style={{ backgroundColor: pal.card }}>
                    <p className="text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                      Improve your recommendations:
                    </p>
                    <ul className="text-sm jd-mute space-y-1">
                      {profileCompleteness.suggestions.map((suggestion, i) => (
                        <li key={i}>• {suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {recommendationsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-pulse space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-24 jd-panel" />
                      ))}
                    </div>
                    <p className="jd-mute mt-4">Finding your best matches...</p>
                  </div>
                ) : recommendedJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="jd-empty-mark" aria-hidden />
                    <h3 className="jd-h3 mb-2" style={{ color: pal.ink }}>
                      No Recommendations Yet
                    </h3>
                    <p className="jd-mute mb-6">
                      Complete your profile to get personalized job recommendations.
                    </p>
                    <button
                      onClick={() => setActiveTab('profile')}
                      className="inline-flex items-center px-6 py-3 jd-on font-medium transition-all hover:opacity-90"
                      style={{ backgroundColor: pal.accent }}
                    >
                      Complete Profile
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recommendedJobs.map(({ job, matchScore, matchReasons }) => (
                      <div key={job.id} className="p-6 border hover: transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="jd-h4">{job.title}</h3>
                              {job.hot && <span className="jd-hot">Hot</span>}
                              <div className="flex items-center gap-1">
                                <div className="w-16 h-2 jd-panel">
                                  <div
                                    className="h-2 transition-all"
                                    style={{
                                      width: `${matchScore}%`,
                                      backgroundColor: matchScore >= 90 ? pal.accent :
                                                      matchScore >= 80 ? pal.accent : pal.inkSoft
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium" style={{ color: pal.accent }}>
                                  {matchScore}%
                                </span>
                              </div>
                            </div>
                            <p className="jd-mute mb-2">{job.company} • {job.location}</p>
                            <p className="text-sm jd-mute mb-2">{job.salary} • {job.employmentType}</p>
                            {matchReasons.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs jd-mute">Why recommended:</span>
                                {matchReasons.map((reason, i) => (
                                  <span
                                    key={i}
                                    className="text-xs px-2 py-1"
                                    style={{ backgroundColor: pal.card, color: pal.inkSoft }}
                                  >
                                    {reason}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => navigate(`/jobs/${job.id}`)}
                              className="px-4 py-2 border font-medium transition-all hover:opacity-80"
                              style={{ borderColor: pal.rule, color: pal.inkSoft }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => navigate(`/jobs/${job.id}`)}
                              className="px-4 py-2 jd-on font-medium transition-all hover:opacity-90"
                              style={{ backgroundColor: pal.accent }}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'profile' && (
              <div style={{ ...acaCard, padding: 32 }}>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="jd-h2" style={{ color: pal.ink }}>
                      My Profile
                    </h2>
                    <p className="jd-mute mt-1">Manage your professional information</p>
                  </div>
                  <div className="flex gap-3">
                    {!isEditing ? (
                      <button
                        onClick={handleEdit}
                        className="px-6 py-3 jd-on font-medium transition-all hover:opacity-90"
                        style={{ backgroundColor: pal.accent }}
                      >
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex gap-3">
                        <button 
                          onClick={handleCancel}
                          className="px-6 py-3 border font-medium transition-all hover:opacity-80" 
                          style={{ borderColor: pal.rule, color: pal.inkSoft }}
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={handleSave}
                          disabled={profileLoading}
                          className="px-6 py-3 jd-on font-medium transition-all hover:opacity-90 disabled:opacity-50" 
                          style={{ backgroundColor: pal.accent }}
                        >
                          {profileLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {profileLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: pal.accent }}></div>
                      <p className="jd-mute">Loading your profile...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Basic Information */}
                    <div style={{ ...acaCard }}>
                      <h3 className="jd-h3 mb-6" style={{ color: pal.ink }}>
                        Basic Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            First Name *
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              name="firstName"
                              value={editFormData.firstName || user?.firstName || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.firstName || user?.firstName || 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Last Name *
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              name="lastName"
                              value={editFormData.lastName || user?.lastName || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.lastName || user?.lastName || 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Email Address
                          </label>
                          <p className="text-lg font-medium" style={{ color: pal.ink }}>
                            {user?.email || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Phone Number *
                          </label>
                          {isEditing ? (
                            <input
                              type="tel"
                              name="phoneNumber"
                              value={editFormData.phoneNumber || user?.phoneNumber || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.phoneNumber || user?.phoneNumber || 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Address
                          </label>
                          {isEditing ? (
                            <textarea
                              name="address"
                              value={editFormData.address || ''}
                              onChange={handleInputChange}
                              rows={3}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all resize-none"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.address || 'Not provided'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Professional Information */}
                    <div style={{ ...acaCard }}>
                      <h3 className="jd-h3 mb-6" style={{ color: pal.ink }}>
                        Professional Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Academic roles you're available for
                          </label>
                          {isEditing ? (
                            <div className="grid grid-cols-2 gap-2">
                              {ACADEMIC_ROLES.map((role) => {
                                const checked = (editFormData.academicRoles || []).includes(role);
                                return (
                                  <label
                                    key={role}
                                    className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-gray-200 hover:jd-panel jd-panel"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const current = editFormData.academicRoles || [];
                                        const next = e.target.checked
                                          ? [...current, role]
                                          : current.filter((r: string) => r !== role);
                                        setEditFormData((prev: any) => ({ ...prev, academicRoles: next }));
                                      }}
                                      className="h-4 w-4 rounded" style={{ accentColor: pal.ink }}
                                    />
                                    <span>{role}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {(profileData?.academicRoles || []).length > 0
                                ? (profileData.academicRoles as string[]).join(', ')
                                : 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Study levels you can teach at
                          </label>
                          {isEditing ? (
                            <StudyLevelMultiSelect
                              value={editFormData.studyLevels || []}
                              onChange={(next) =>
                                setEditFormData((prev: any) => ({ ...prev, studyLevels: next }))
                              }
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {(profileData?.studyLevels || []).length > 0
                                ? (profileData.studyLevels as string[]).join(', ')
                                : 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Academic subjects
                          </label>
                          {isEditing ? (
                            <SubjectMultiSelect
                              value={editFormData.subjects || []}
                              onChange={(next) =>
                                setEditFormData((prev: any) => ({ ...prev, subjects: next }))
                              }
                              placeholder="Search and add subjects…"
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {(profileData?.subjects || []).length > 0
                                ? (profileData.subjects as string[]).join(', ')
                                : 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            HE Recognition
                          </label>
                          {isEditing ? (
                            <FolioSelect
                              name="professionalAccreditation"
                              ariaLabel="HE recognition"
                              placeholder="Select your accreditation"
                              value={editFormData.professionalAccreditation || ''}
                              onChange={(v) =>
                                setEditFormData((prev: any) => ({ ...prev, professionalAccreditation: v }))
                              }
                              options={HE_RECOGNITION_OPTIONS}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.professionalAccreditation || 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="eligibleToWorkInUK"
                              checked={editFormData.eligibleToWorkInUK || false}
                              onChange={handleInputChange}
                              disabled={!isEditing}
                              className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                              style={{}}
                            />
                            <label className="ml-3 text-sm font-medium" style={{ color: pal.inkSoft }}>
                              Eligible to Work in UK
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documents & Certifications */}
                    <div style={{ ...acaCard }}>
                      <h3 className="jd-h3 mb-6" style={{ color: pal.ink }}>
                        Documents & Certifications
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            DBS Certificate Number
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              name="dbsCertificateNumber"
                              value={editFormData.dbsCertificateNumber || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.dbsCertificateNumber || 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            DBS Expiry Date
                          </label>
                          {isEditing ? (
                            <input
                              type="date"
                              name="dbsExpiryDate"
                              value={editFormData.dbsExpiryDate ? new Date(editFormData.dbsExpiryDate).toISOString().split('T')[0] : ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.dbsExpiryDate ? new Date(profileData.dbsExpiryDate).toLocaleDateString() : 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            National Insurance Number
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              name="nationalInsuranceNumber"
                              value={editFormData.nationalInsuranceNumber || ''}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border focus:outline-none focus:ring-2 transition-all"
                              style={{ borderColor: pal.rule }}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.nationalInsuranceNumber || 'Not provided'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            How did you hear about us?
                          </label>
                          {isEditing ? (
                            <FolioSelect
                              name="heardAboutUs"
                              ariaLabel="How did you hear about us?"
                              placeholder="Select an option"
                              value={editFormData.heardAboutUs || ''}
                              onChange={(v) =>
                                setEditFormData((prev: any) => ({ ...prev, heardAboutUs: v }))
                              }
                              options={HEARD_ABOUT_OPTIONS}
                            />
                          ) : (
                            <p className="text-lg font-medium" style={{ color: pal.ink }}>
                              {profileData?.heardAboutUs || 'Not provided'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* File Uploads */}
                    <div style={{ ...acaCard }}>
                      <h3 className="jd-h3 mb-6" style={{ color: pal.ink }}>
                        File Uploads
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            CV/Resume
                          </label>
                          <div className="flex items-center gap-3">
                            <span className={`${
                              profileData?.cvUrl ? 'jd-chip jd-chip-yes' : 'jd-chip'
                            }`}>
                              {profileData?.cvUrl ? 'Uploaded' : 'Not uploaded'}
                            </span>
                            {isEditing && (
                              <button className="px-4 py-2 border font-medium transition-all hover:opacity-80" style={{ borderColor: pal.rule, color: pal.inkSoft }}>
                                Upload
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: pal.inkSoft }}>
                            Degree Certificate
                          </label>
                          <div className="flex items-center gap-3">
                            <span className={`${
                              profileData?.degreeUrl ? 'jd-chip jd-chip-yes' : 'jd-chip'
                            }`}>
                              {profileData?.degreeUrl ? 'Uploaded' : 'Not uploaded'}
                            </span>
                            {isEditing && (
                              <button className="px-4 py-2 border font-medium transition-all hover:opacity-80" style={{ borderColor: pal.rule, color: pal.inkSoft }}>
                                Upload
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setJobToDelete(null);
        }}
        onConfirm={confirmRemoveSavedJob}
        title="Remove Saved Job"
        message={`Are you sure you want to remove "${jobToDelete?.title}" from your saved jobs? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />

      <style>{`
        /* The margin stays in view while the panel beside it scrolls. The
           aside must stretch to the row: a sticky child inside a content-
           height parent has no distance to travel and never pins. */
        .jd-root .jd-sticky {
          position: sticky;
          top: 84px;
          max-height: calc(100vh - 108px);
          overflow-y: auto;
          background: ${T.ground};
        }
        .jd-root .jd-nav { transition: color 140ms ease; }
        .jd-root .jd-nav:hover { color: ${T.accent}; }

        @media (max-width: 980px) {
          .jd-root .jd-grid { grid-template-columns: 1px minmax(0, 1fr) !important; column-gap: 0 !important; }
          .jd-root .jd-spine { grid-column: 1 !important; }
          .jd-root .jd-measure, .jd-root .jd-margin { grid-column: 2 !important; padding-left: 22px !important; }
          /* Navigate leads: on a phone it is the control, not an annotation.
             These must win over the inline row placement, which an ordinary
             class rule would lose to. */
          .jd-root .jd-margin { grid-row: 1 !important; }
          .jd-root .jd-measure { grid-row: 2 !important; padding-top: 34px; }
          .jd-root .jd-sticky { position: static !important; max-height: none !important; overflow: visible !important; }
        }

        /* The type scale, applied by class so the markup stays readable. */
        .jd-root .jd-h2 { font-family: ${SANS}; font-weight: 700; font-size: clamp(24px, 3vw, 30px); letter-spacing: -0.03em; line-height: 1.1; color: ${T.ink}; }
        .jd-root .jd-h3 { font-family: ${SANS}; font-weight: 700; font-size: 20px; letter-spacing: -0.02em; line-height: 1.2; color: ${T.ink}; }
        .jd-root .jd-h4 { font-family: ${SANS}; font-weight: 700; font-size: 17px; letter-spacing: -0.015em; line-height: 1.25; color: ${T.ink}; }
        .jd-root .jd-figure { font-family: ${MONO}; font-size: 30px; font-weight: 500; color: ${T.ink}; line-height: 1; }

        .jd-root .jd-mute { color: ${T.mute}; }
        .jd-root .jd-ink { color: ${T.ink}; }
        .jd-root .jd-on { color: ${T.onAccent}; }
        .jd-root .jd-panel { background: ${T.ground}; }

        /* A status is mono text with a drawn mark, never a pastel pill.
           The word itself carries the state, so colour is never the only
           thing distinguishing one from another. */
        .jd-root .jd-chip {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: ${MONO}; font-size: 10px; font-weight: 500;
          letter-spacing: 0.14em; text-transform: uppercase;
          border: 1px solid ${T.rule}; color: ${T.mute};
          padding: 5px 10px; white-space: nowrap;
        }
        .jd-root .jd-chip::before { content: ""; width: 5px; height: 5px; background: currentColor; flex-shrink: 0; }
        .jd-root .jd-chip-yes  { color: ${T.accent}; border-color: ${T.accent}; }
        .jd-root .jd-chip-no   { color: ${T.ink};    border-color: ${T.ink}; }
        .jd-root .jd-chip-wait { color: ${T.mute};   border-color: ${T.rule}; }

        .jd-root .jd-hot {
          display: inline-flex; align-items: center;
          font-family: ${MONO}; font-size: 9.5px; font-weight: 500;
          letter-spacing: 0.16em; text-transform: uppercase;
          color: ${T.onAccent}; background: ${T.accent};
          padding: 4px 8px;
        }

        /* An empty state gets a registration mark, drawn, rather than a
           six-storey emoji. */
        .jd-root .jd-empty-mark {
          width: 44px; height: 44px; margin: 0 auto 18px;
          border: 1.5px solid ${T.rule}; position: relative;
        }
        .jd-root .jd-empty-mark::before,
        .jd-root .jd-empty-mark::after { content: ""; position: absolute; background: ${T.rule}; }
        .jd-root .jd-empty-mark::before { left: 9px; right: 9px; top: 50%; height: 1px; }
        .jd-root .jd-empty-mark::after  { top: 9px; bottom: 9px; left: 50%; width: 1px; }

        .jd-root .jd-accent { transition: background 150ms ease; }
        .jd-root .jd-accent:hover { background: ${T.accentDeep}; }
        .jd-root .jd-accent:focus-visible,
        .jd-root button:focus-visible,
        .jd-root a:focus-visible,
        .jd-root input:focus-visible,
        .jd-root select:focus-visible,
        .jd-root textarea:focus-visible {
          outline: 2px solid ${T.accent}; outline-offset: 2px;
        }

        .jd-root input::placeholder,
        .jd-root textarea::placeholder { color: ${T.mute}; opacity: 1; }

        /* The skeleton pulses on the sheet's own rule colour. */
        @keyframes jd-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }
        .jd-root .animate-pulse { animation: jd-pulse 1.6s infinite; }

        @media (prefers-reduced-motion: reduce) {
          .jd-root .animate-pulse { animation: none; }
          .jd-root .jd-accent { transition: none; }
        }
      `}</style>
    </SheetGround>
  );
};

export default JobSeekerDashboardPage;
